import { useState, useEffect, useMemo } from 'react';
import { Plus, Edit2, Trash2, Save, Tv, Radio, Link as LinkIcon, Trash, Search, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, ChevronsUpDown, X, Globe, Eye, EyeOff } from 'lucide-react';

interface Channel {
  uuid: string;
  name: string;
  description?: string;
  type: string;
  category: string;
  stream_type: string;
  stream_url: string;
  epg_channel_id?: string;
  logo_url: string;
  country: string;
  language: string;
  is_active: number;
  headers?: any;
  drm?: any;
}

type SortField = 'name' | 'type' | 'category' | 'is_active' | 'country' | 'language';
type SortDir = 'asc' | 'desc';

export default function LiveTvManager() {
  const API_URL = import.meta.env.VITE_API_URL || 'https://heritage-os-api.uplaytv3117.workers.dev/api/v1';

  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentChannel, setCurrentChannel] = useState<Partial<Channel>>({
    type: 'TV', category: 'General', country: 'Morocco', language: 'ar', stream_type: 'hls', is_active: 1
  });
  const [headersInput, setHeadersInput] = useState('{}');
  const [drmInput, setDrmInput] = useState('{}');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  useEffect(() => {
    fetchChannels();
  }, []);

  const fetchChannels = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/iptv/admin/channels`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setChannels(data);
      }
    } catch (error) {
      console.error('Failed to fetch channels', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronsUpDown size={14} className="text-slate-300" />;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('token');
      const isNew = !currentChannel.uuid;
      const url = isNew
        ? `${API_URL}/iptv/channels`
        : `${API_URL}/iptv/channels/${currentChannel.uuid}`;

      let parsedHeaders = null;
      let parsedDrm = null;
      try {
        if (headersInput.trim() && headersInput.trim() !== '{}') parsedHeaders = JSON.parse(headersInput);
        if (drmInput.trim() && drmInput.trim() !== '{}') parsedDrm = JSON.parse(drmInput);
      } catch (e) {
        alert('Invalid JSON in Headers or DRM field');
        return;
      }

      const payload = { ...currentChannel, headers: parsedHeaders, drm: parsedDrm };

      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setIsEditing(false);
        fetchChannels();
      } else alert('Failed to save channel');
    } catch (error) {
      console.error('Failed to save channel', error);
    }
  };

  const handleDelete = async (uuid: string) => {
    if (!window.confirm('Are you sure you want to delete this channel?')) return;
    try {
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/iptv/channels/${uuid}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchChannels();
        setSelectedIds(prev => prev.filter(id => id !== uuid));
      }
    } catch (error) {
      console.error('Failed to delete channel', error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} channels?`)) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('token');
      await Promise.all(selectedIds.map(uuid =>
        fetch(`${API_URL}/iptv/channels/${uuid}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ));
      setSelectedIds([]);
      await fetchChannels();
    } catch (error) {
      console.error('Failed to bulk delete', error);
    } finally {
      setLoading(false);
    }
  };

  const copyApiLink = (uuid: string) => {
    const link = `${API_URL}/iptv/channels?id=${uuid}`;
    navigator.clipboard.writeText(link);
    alert('API link copied!');
  };

  const openEditor = (channel?: Channel) => {
    if (channel) {
      setCurrentChannel(channel);
      setHeadersInput(channel.headers ? JSON.stringify(channel.headers, null, 2) : '{}');
      setDrmInput(channel.drm ? JSON.stringify(channel.drm, null, 2) : '{}');
    } else {
      setCurrentChannel({
        type: 'TV', category: 'General', country: 'Morocco', language: 'ar', stream_type: 'hls', is_active: 1
      });
      setHeadersInput('{}');
      setDrmInput('{}');
    }
    setIsEditing(true);
  };

  const filteredChannels = useMemo(() => {
    let list = channels.filter(ch => {
      const s = search.toLowerCase();
      const matchesSearch = ch.name.toLowerCase().includes(s) || ch.stream_url?.toLowerCase().includes(s) || ch.uuid?.toLowerCase().includes(s);
      const matchesType = typeFilter ? ch.type === typeFilter : true;
      const matchesCategory = categoryFilter ? ch.category === categoryFilter : true;
      const matchesStatus = statusFilter === 'active' ? ch.is_active === 1 : statusFilter === 'inactive' ? ch.is_active === 0 : true;
      return matchesSearch && matchesType && matchesCategory && matchesStatus;
    });

    list.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      return String(aVal).localeCompare(String(bVal), undefined, { numeric: sortField === 'is_active' }) * dir;
    });

    return list;
  }, [channels, search, typeFilter, categoryFilter, statusFilter, sortField, sortDir]);

  const categories = useMemo(() => Array.from(new Set(channels.map(c => c.category).filter(Boolean))), [channels]);
  const totalPages = Math.ceil(filteredChannels.length / itemsPerPage) || 1;
  const currentChannels = filteredChannels.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const toggleAll = () => {
    if (selectedIds.length === currentChannels.length) setSelectedIds([]);
    else setSelectedIds(currentChannels.map(c => c.uuid));
  };

  const toggleRow = (uuid: string) => {
    setSelectedIds(prev => prev.includes(uuid) ? prev.filter(x => x !== uuid) : [...prev, uuid]);
  };

  return (
    <div className="max-w-7xl mx-auto p-6 xl:p-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Tv className="w-6 h-6 text-indigo-500" />
            Live TV & Radio Manager
          </h1>
          <p className="text-sm text-slate-500 mt-1">{channels.length} channels · {filteredChannels.length} shown</p>
        </div>
        <div className="flex gap-3">
          {selectedIds.length > 0 && (
            <button onClick={handleBulkDelete} className="px-4 py-2.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-lg text-sm font-medium hover:bg-rose-100 transition-colors flex items-center gap-2">
              <Trash size={16} /> Delete {selectedIds.length}
            </button>
          )}
          <button onClick={() => openEditor()}
            className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm text-sm font-medium">
            <Plus size={18} /> Add Channel
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input type="text" placeholder="Search by name, URL, or ID..." value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm bg-white" />
          </div>
          <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2.5 rounded-lg border border-slate-300 bg-white text-sm outline-none focus:ring-2 focus:ring-indigo-500">
            <option value="">All Types</option>
            <option value="TV">TV</option>
            <option value="Radio">Radio</option>
          </select>
          <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2.5 rounded-lg border border-slate-300 bg-white text-sm outline-none focus:ring-2 focus:ring-indigo-500">
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2.5 rounded-lg border border-slate-300 bg-white text-sm outline-none focus:ring-2 focus:ring-indigo-500">
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="p-4 w-10">
                  <input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    checked={currentChannels.length > 0 && selectedIds.length === currentChannels.length} onChange={toggleAll} />
                </th>
                <th className="p-4 text-left">
                  <button onClick={() => toggleSort('name')} className="flex items-center gap-1 text-xs font-semibold text-slate-600 uppercase tracking-wider hover:text-slate-900">
                    Channel <SortIcon field="name" />
                  </button>
                </th>
                <th className="p-4 text-left hidden md:table-cell">
                  <button onClick={() => toggleSort('type')} className="flex items-center gap-1 text-xs font-semibold text-slate-600 uppercase tracking-wider hover:text-slate-900">
                    Type <SortIcon field="type" />
                  </button>
                </th>
                <th className="p-4 text-left hidden lg:table-cell">
                  <button onClick={() => toggleSort('category')} className="flex items-center gap-1 text-xs font-semibold text-slate-600 uppercase tracking-wider hover:text-slate-900">
                    Category <SortIcon field="category" />
                  </button>
                </th>
                <th className="p-4 text-left hidden xl:table-cell">
                  <button onClick={() => toggleSort('country')} className="flex items-center gap-1 text-xs font-semibold text-slate-600 uppercase tracking-wider hover:text-slate-900">
                    Country <SortIcon field="country" />
                  </button>
                </th>
                <th className="p-4 text-left">
                  <button onClick={() => toggleSort('is_active')} className="flex items-center gap-1 text-xs font-semibold text-slate-600 uppercase tracking-wider hover:text-slate-900">
                    Status <SortIcon field="is_active" />
                  </button>
                </th>
                <th className="p-4 text-left hidden lg:table-cell">
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Stream</span>
                </th>
                <th className="p-4 text-right w-32">
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-500">Loading channels...</td>
                </tr>
              ) : currentChannels.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-12 text-center">
                    <div className="flex flex-col items-center text-slate-400">
                      <Tv size={40} className="mb-3 opacity-50" />
                      <p className="font-medium text-slate-500">No channels found</p>
                      <p className="text-sm mt-1">Try adjusting your filters or add a new channel.</p>
                    </div>
                  </td>
                </tr>
              ) : currentChannels.map(channel => (
                <tr key={channel.uuid} className={`hover:bg-slate-50 transition-colors group ${selectedIds.includes(channel.uuid) ? 'bg-indigo-50/50' : ''}`}>
                  <td className="p-4"><input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    checked={selectedIds.includes(channel.uuid)} onChange={() => toggleRow(channel.uuid)} /></td>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {channel.logo_url ? (
                        <img src={channel.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover border border-slate-200 bg-slate-900 flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center border border-slate-200 flex-shrink-0">
                          {channel.type === 'Radio' ? <Radio className="w-5 h-5 text-slate-400" /> : <Tv className="w-5 h-5 text-slate-400" />}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate max-w-[200px]">{channel.name}</p>
                        {channel.description && <p className="text-xs text-slate-400 truncate max-w-[200px]">{channel.description}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="p-4 hidden md:table-cell">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      channel.type === 'Radio' ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'bg-blue-50 text-blue-700 border border-blue-200'
                    }`}>
                      {channel.type === 'Radio' ? <Radio size={12} /> : <Tv size={12} />}
                      {channel.type === 'Radio' ? 'Radio' : 'TV'}
                    </span>
                  </td>
                  <td className="p-4 hidden lg:table-cell text-slate-600 text-xs">{channel.category}</td>
                  <td className="p-4 hidden xl:table-cell text-slate-600 text-xs font-medium">{channel.country}</td>
                  <td className="p-4">
                    {channel.is_active === 1 ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <Eye size={12} /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                        <EyeOff size={12} /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="p-4 hidden lg:table-cell">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      channel.stream_type === 'hls' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                      channel.stream_type === 'mpd' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' :
                      'bg-slate-50 text-slate-600 border border-slate-200'
                    }`}>
                      {channel.stream_type.toUpperCase()}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => copyApiLink(channel.uuid)}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Copy API Link">
                        <LinkIcon size={16} />
                      </button>
                      <a href={`${API_URL}/iptv/channels?id=${channel.uuid}`} target="_blank" rel="noreferrer"
                        className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" title="View API JSON">
                        <Globe size={16} />
                      </a>
                      <button onClick={() => openEditor(channel)}
                        className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Edit Channel">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => handleDelete(channel.uuid)}
                        className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" title="Delete Channel">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex justify-between items-center text-xs text-slate-500">
          <span>{(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, filteredChannels.length)} of {filteredChannels.length}</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed bg-white">
              <ChevronLeft size={16} />
            </button>
            <span className="font-medium text-slate-700 px-1">Page {currentPage} of {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed bg-white">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-4 z-40 animate-in slide-in-from-bottom-5">
          <span className="font-medium">{selectedIds.length} selected</span>
          <div className="w-px h-6 bg-slate-700"></div>
          <button onClick={handleBulkDelete} className="hover:text-red-400 flex items-center gap-2 transition-colors text-sm font-medium">
            <Trash size={16} /> Delete
          </button>
        </div>
      )}

      {isEditing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-6 border-b border-slate-100 pb-4">
              <h2 className="text-lg font-bold text-slate-800">{currentChannel.uuid ? 'Edit Channel' : 'Add Channel'}</h2>
              <button onClick={() => setIsEditing(false)} className="text-slate-400 hover:text-slate-700 transition-colors"><X size={24} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Channel Name</label>
                <input type="text" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                  value={currentChannel.name || ''} onChange={e => setCurrentChannel({...currentChannel, name: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <input type="text" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                  value={currentChannel.description || ''} onChange={e => setCurrentChannel({...currentChannel, description: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                <select className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                  value={currentChannel.type || 'TV'} onChange={e => setCurrentChannel({...currentChannel, type: e.target.value})}>
                  <option value="TV">TV Channel</option>
                  <option value="Radio">Radio Station</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Stream Type</label>
                <select className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                  value={currentChannel.stream_type || 'hls'} onChange={e => setCurrentChannel({...currentChannel, stream_type: e.target.value})}>
                  <option value="hls">HLS (.m3u8)</option>
                  <option value="mpd">DASH (.mpd)</option>
                  <option value="rtmp">RTMP</option>
                  <option value="mp4">MP4</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                <select className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                  value={currentChannel.category || 'General'} onChange={e => setCurrentChannel({...currentChannel, category: e.target.value})}>
                  <option value="General">General</option>
                  <option value="News">News</option>
                  <option value="Entertainment">Entertainment</option>
                  <option value="Sports">Sports</option>
                  <option value="Music">Music</option>
                  <option value="Religion">Religion</option>
                  <option value="MBC">MBC</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">EPG Channel ID</label>
                <input type="text" placeholder="e.g. MBCksa"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                  value={currentChannel.epg_channel_id || ''} onChange={e => setCurrentChannel({...currentChannel, epg_channel_id: e.target.value})} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Stream URL</label>
                <input type="text" placeholder="https://.../stream.m3u8"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-indigo-500"
                  value={currentChannel.stream_url || ''} onChange={e => setCurrentChannel({...currentChannel, stream_url: e.target.value})} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Logo URL</label>
                <input type="text"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-indigo-500"
                  value={currentChannel.logo_url || ''} onChange={e => setCurrentChannel({...currentChannel, logo_url: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
                <input type="text"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                  value={currentChannel.country || ''} onChange={e => setCurrentChannel({...currentChannel, country: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Language</label>
                <input type="text"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                  value={currentChannel.language || ''} onChange={e => setCurrentChannel({...currentChannel, language: e.target.value})} />
              </div>
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-slate-700 mb-1">Headers (JSON)</label>
                <textarea rows={5}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 font-mono text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                  value={headersInput} onChange={e => setHeadersInput(e.target.value)}
                  placeholder='{\n  "User-Agent": "...",\n  "Referer": "..."\n}' />
              </div>
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-slate-700 mb-1">DRM Configuration (JSON)</label>
                <textarea rows={5}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 font-mono text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                  value={drmInput} onChange={e => setDrmInput(e.target.value)}
                  placeholder='{\n  "type": "widevine",\n  "license_url": "..."\n}' />
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="rounded text-indigo-600 border-slate-300 focus:ring-indigo-500 h-4 w-4"
                    checked={currentChannel.is_active === 1}
                    onChange={e => setCurrentChannel({...currentChannel, is_active: e.target.checked ? 1 : 0})} />
                  <span className="text-sm font-medium text-slate-700">Channel Active (Visible in App)</span>
                </label>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3 border-t border-slate-100 pt-4">
              <button onClick={() => setIsEditing(false)}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm">
                Cancel
              </button>
              <button onClick={handleSave}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center text-sm font-medium">
                <Save className="w-4 h-4 mr-2" />
                Save Channel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
